import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { showContextMenu } from '@/shell/ContextMenu/ContextMenu';
import { taskbarMenu } from '@/shell/ContextMenu/menus';
import { selectTaskbarWindows, useWindowStore } from '@/wm/store';
import { StartMenu } from '@/shell/StartMenu/StartMenu';
import { Tray } from '@/shell/Tray/Tray';
import { StartButton } from './StartButton';
import { TaskButton } from './TaskButton';
import { TaskGroupButton } from './TaskGroupButton';
import { groupTasks, taskCapacity } from './grouping';
import css from './Taskbar.module.css';

export function Taskbar() {
  // Subscribe to the raw array — `selectTaskbarWindows` builds a fresh one
  // every call, which would defeat zustand's snapshot comparison. Memoising
  // off the array keeps the buttons from remounting on unrelated store churn.
  const windows = useWindowStore((s) => s.windows);
  const tasks = useMemo(() => selectTaskbarWindows(useWindowStore.getState()), [windows]);

  const [startOpen, setStartOpen] = useState(false);
  const startRef = useRef<HTMLButtonElement>(null);

  /* --------------------------------------------------------------- grouping */

  const stripRef = useRef<HTMLDivElement>(null);
  const [stripWidth, setStripWidth] = useState(0);

  // The strip is what decides how many buttons fit, and it changes width when
  // the tray does — a longer clock is a narrower strip — so measuring it is
  // the only honest way to know.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    setStripWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (typeof next === 'number') setStripWidth(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const items = useMemo(
    () => groupTasks(tasks, taskCapacity(stripWidth)),
    [tasks, stripWidth],
  );

  const toggleStart = useCallback(() => setStartOpen((v) => !v), []);
  const closeStart = useCallback(() => setStartOpen(false), []);

  /**
   * "The taskbar" means the bar itself or the empty run beside the task
   * buttons. A task button raises its own menu and stops the event before it
   * gets here; the Start menu renders inside this subtree too, and its rows are
   * none of our business. Nothing in the bar gets the browser's own menu either
   * way — a Chrome popup over the desktop would give the whole game away.
   */
  function handleContextMenu(event: ReactMouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    event.preventDefault();
    if (target !== event.currentTarget && !target.classList.contains(css.tasks)) return;
    showContextMenu(event, taskbarMenu());
  }

  return (
    <div className={css.taskbar} onContextMenu={handleContextMenu}>
      <StartButton ref={startRef} open={startOpen} onToggle={toggleStart} />
      <StartMenu open={startOpen} onClose={closeStart} anchorRef={startRef} />

      <div ref={stripRef} className={css.tasks}>
        {items.map((item) =>
          item.kind === 'window' ? (
            <TaskButton key={item.win.id} win={item.win} />
          ) : (
            <TaskGroupButton key={item.appId} appId={item.appId} windows={item.windows} />
          ),
        )}
      </div>

      <Tray />
    </div>
  );
}
